import { createSelector } from 'reselect';
import { cloneDeep, isEmpty, mean, get, round, reduce, uniqBy } from 'lodash';

import orgHierarchy2 from '../data/viewDemo/1_org_hierarchy_new.json';
import roleDetails2 from '../data/viewDemo/2_role_details_new.json';
import successProfiles2 from '../data/viewDemo/4_success_profile_new.json';
import userSkillProfiles2 from '../data/viewDemo/6_user_skill_profile_new.json';
import skillDetails2 from '../data/viewDemo/7_skill_details_new.json';
import userDetails2 from '../data/viewDemo/8_user_details_new.json';
import userJourney from '../data/viewDemo/9_user_journey_report_new.json';
import skillHealth from '../data/viewDemo/10_skill_health.json';

const getFlatHierarchy = () => {};
const getSelectedRole = state =>
  state.routes.viewDemo.flowConfig.selectedRoleId;

const getSelectedUser = state => state.routes.viewDemo.flowConfig.selectedUser;

export const getAppliedFilter = state => state.routes.viewDemo.flowConfig.appliedFilter;

const getUserHealth = (userId, profileId) => {
  const successProf = successProfiles2.filter(d => d.Role === profileId);
  const userSkillProf = userSkillProfiles2.filter(
    d => d['Employee ID'] === userId,
  );
  if (isEmpty(successProf) || isEmpty(userSkillProf)) {
    return 100;
  }

  const weightConstant = 0.25;
  const primarySkills = successProf.filter(
    d => d['Primary(Must Have)/Secondary(Good to have)'] === 'Primary',
  );
  const primaryCount = primarySkills.length;
  const secondarySkills = successProf.filter(
    d => d['Primary(Must Have)/Secondary(Good to have)'] !== 'Primary',
  );
  const secondaryCount = secondarySkills.length;
  const primaryWeight = 100 / (primaryCount + weightConstant * secondaryCount);
  const secondaryWeight = primaryWeight * weightConstant;
  const primaryHealthArray = primarySkills.map(skll => {
    const reqLevel = skll['Proficiency Level Required'];
    const currSkill = userSkillProf.find(
      d => d['Skill/Competency Name'] === skll['Skill Name'],
    );
    const currLevel = currSkill['Overall Rating'];
    return round(
      Math.min((currLevel * primaryWeight) / reqLevel, primaryWeight),
    );
  });
  const secondaryHealthArray = secondarySkills.map(skll => {
    const reqLevel = skll['Proficiency Level Required'];
    const currSkill = userSkillProf.find(
      d => d['Skill/Competency Name'] === skll['Skill Name'],
    );
    const currLevel = currSkill['Overall Rating'];
    return round(
      Math.min((currLevel * secondaryWeight) / reqLevel, secondaryWeight),
    );
  });

  const totalHealth = [...primaryHealthArray, ...secondaryHealthArray].reduce(
    (a, b) => a + b,
  );
  return round(totalHealth);
};

const getRoleHealth = profileId => {
  const users = userDetails2.filter(d => d.Role === profileId);
  if (!users || !profileId) {
    return 100;
  }

  const roleHealthArray = users.map(d =>
    getUserHealth(d.EmployeeID, profileId),
  );

  return mean(roleHealthArray);
};

export const getRoleFlattenedHierarchy = () => {
  const transformedHierarchy = cloneDeep(orgHierarchy2);
  const getUserCount = profileId =>
    userDetails2.filter(d => d.Role === profileId).length;
  transformedHierarchy.forEach(d => {
    d.health = d.locked ? 0 : getRoleHealth(d.id);
    d.users = d.locked ? 0 : getUserCount(d.id);
    d.color = d.locked
      ? 'rgba(var(--bg-primary), 0)'
      : getRoleHealth(d.id) > 70
      ? 'rgb(var(--accent-success))'
      : getRoleHealth(d.id) > 30
      ? 'rgb(var(--accent-warning))'
      : 'rgb(var(--accent-danger))';
  });
  return transformedHierarchy;
};

const getOneSkillHealth = skillName => {
  const rolesWithThisSkill = successProfiles2.filter(
    d => d['Skill Name'] === skillName,
  );
  let skillHealthArray = [];

  rolesWithThisSkill.forEach(rol => {
    const reqLevel = rol['Proficiency Level Required'];
    const usersInThisRole = userSkillProfiles2.filter(
      d =>
        d.Role === rol.Role && d['Skill/Competency Name'] === skillName,
    );
    usersInThisRole.forEach(usr => {
      const currLevel = usr['Overall Rating'];
      const gap =
        reqLevel > currLevel ? ((reqLevel - currLevel) * 100) / reqLevel : 0;
      skillHealthArray = [...skillHealthArray, 100 - gap];
    });
  });
  return round(mean(skillHealthArray));
};

const getSkillCoreStatus = skillName => {
  if (!skillName) {
    return false;
  }
  const rolesWithThisSkill = successProfiles2.filter(
    d => d['Skill Name'] === skillName,
  );

  return rolesWithThisSkill.some(
    rol => rol['Primary(Must Have)/Secondary(Good to have)'] === 'Primary',
  );
};

const getSkillAssociatedRoles = skillName => {
  if (!skillName) {
    return 0;
  }
  const rolesWithThisSkill = successProfiles2.filter(
    d => d['Skill Name'] === skillName,
  );

  return rolesWithThisSkill.length;
};

const getSkillAssociatedUsers = skillName => {
  if (!skillName) {
    return 0;
  }
  const rolesWithThisSkill = successProfiles2.filter(
    d => d['Skill Name'] === skillName,
  );
  let users = [];

  rolesWithThisSkill.forEach(rol => {
    const usersInThisRole = userSkillProfiles2.filter(
      d =>
        d.Role === rol.Role && d['Skill/Competency Name'] === skillName,
    );
    users = [...users, ...usersInThisRole];
  });
  return users.length;
};

export const getSkillHealth = () => {
  const uniqueSkills = uniqBy(skillDetails2, d => d['Competency Name']);
  let normalizedSkills = [];

  uniqueSkills.forEach(skll => {
    const health = getOneSkillHealth(skll['Competency Name']);

    if (health) {
      const tempSkill = {
        id: skll['Competency Name'],
        name: skll['Competency Name'],
        entityType: 'skill',
        description: get(skll, 'Competency Description', ''),
        nature: get(skll, 'Skill Nature', 'Emerging'),
        category: get(
          skll,
          'Competency Type (Technical/Behavioral)',
          'Technical',
        ),
        levelDescriptions: {},
        overallHealth: health,
        isCore: getSkillCoreStatus(skll['Competency Name']),
        associatedRolesNumber: getSkillAssociatedRoles(skll['Competency Name']),
        associatedEmployeesNumber: getSkillAssociatedUsers(
          skll['Competency Name'],
        ),
      };
      normalizedSkills = [...normalizedSkills, tempSkill];
    }
  });

  return normalizedSkills;
};

export const getSelectedRoleDetails = createSelector(
  [getSelectedRole, getRoleFlattenedHierarchy],
  (selectedRoleId, roleHierarchy) => {
    const selectedRoleDetailsFromData = roleDetails2.find(
      d => d.id === selectedRoleId,
    );
    const selectedRoleDetailsFromHierarchy = roleHierarchy.find(
      d => d.id === selectedRoleId,
    );

    return {
      ...selectedRoleDetailsFromData,
      ...selectedRoleDetailsFromHierarchy,
    };
  },
);

// export const getRoleHierarchy = () => {
//   const transformedHierarchy = cloneDeep(orgHierarchy);
//
//   // Write Awesome recursive code here
//   const treeWalk = (child, parentId = null) => {
//     if (!child.children) {
//       return;
//     }
//
//     const profileId = child.id;
//     child.details = roleDetails[profileId];
//     child.health = child.locked
//       ? health.roles[profileId]
//       : getRoleHealth(profileId);
//     child.usersCount =
//       child.locked || !roleToUserTagging[profileId]
//         ? 0
//         : (roleToUserTagging[profileId] || 0).length;
//
//     const skills = child.locked
//       ? {}
//       : {
//           ...successProfiles[profileId].primary_skills,
//           ...successProfiles[profileId].secondary_skills,
//         };
//
//     child.skillsCount = Object.keys(skills).length;
//
//     child.children.map(d => treeWalk(d, profileId));
//   };
//   treeWalk(transformedHierarchy);
//   return transformedHierarchy;
// };

export const getOrgHealth = () => {
  const org = getRoleFlattenedHierarchy();
  let weightedHealth = 0;
  let totalUsersCount = 0;
  org.forEach(d => {
    weightedHealth += d.users * d.health;
    totalUsersCount += d.users;
  });
  return weightedHealth / totalUsersCount;
};

export const getSelectedRoleSuccessProfile = createSelector(
  [getSelectedRole],
  selectedRoleId => {
    const successProfileFromData = successProfiles2.filter(
      d => d.Role === selectedRoleId,
    );
    let normalizedSuccessProfile = [];

    successProfileFromData.forEach(skll => {
      const skillDetails = skillDetails2.find(
        d => d['Competency Name'] === skll['Skill Name'],
      );
      const tempSkillDeets = {
        id: skll['Skill Name'],
        name: skll['Skill Name'],
        entityType: 'skill',
        description: get(skillDetails, 'Competency Description', ''),
        nature: get(skillDetails, 'Skill Nature', 'Emerging'),
        category: get(
          skillDetails,
          'Competency Type (Technical/Behavioral)',
          'Technical',
        ),
        levelDescriptions: {},
        requiredLevel: skll['Proficiency Level Required'],
        overallRating: skll['Proficiency Level Required'],
        isPrimary:
          skll['Primary(Must Have)/Secondary(Good to have)'] === 'Primary',
      };
      normalizedSuccessProfile = [...normalizedSuccessProfile, tempSkillDeets];
    });
    return normalizedSuccessProfile;
  },
);

export const getSelectedRoleUsers = createSelector(
  [getSelectedRole],
  selectedRoleId => {
    if (!selectedRoleId) {
      return [];
    }

    const users = userDetails2.filter(d => d.Role === selectedRoleId);
    users.forEach(usr => {
      const health = getUserHealth(usr.EmployeeID, selectedRoleId);
      usr.health = health;
      usr.color =
        health > 70
          ? 'rgb(var(--accent-success))'
          : health > 30
          ? 'rgb(var(--accent-warning))'
          : 'rgb(var(--accent-danger))';
    });

    return users;
  },
);

export const getUserProcessReport = createSelector(
  [getSelectedRoleSuccessProfile, getSelectedUser],
  (successProfile, userDetails) => {
    if (!userDetails) {
      return [];
    }
    const userSkills = userSkillProfiles2.filter(
      d => d['Employee ID'] === userDetails.EmployeeID,
    );

    let normalizeUserProcessReport = [];

    successProfile.forEach(skll => {
      const skillDetailsFromUserSkills = userSkills.find(
        d => d['Skill/Competency Name'] === skll.name,
      );

      const tempSkillDeets = {
        ...skll,
        selfRating: get(skillDetailsFromUserSkills, 'Self Rating', 0),
        managerRating: get(skillDetailsFromUserSkills, 'Manager Rating', 0),
        assessmentRating: get(
          skillDetailsFromUserSkills,
          'Assessment Rating',
          0,
        ),
        overallRating: get(skillDetailsFromUserSkills, 'Overall Rating', 0),
        processStatus: get(
          skillDetailsFromUserSkills,
          'Process Status',
          'Completed',
        ),
      };
      normalizeUserProcessReport = [
        ...normalizeUserProcessReport,
        tempSkillDeets,
      ];
    });

    return normalizeUserProcessReport;
  },
);

export const getUserJourneyReport = createSelector(
  [getSelectedUser],
  userDetails => {
    const userId = userDetails.EmployeeID;
    const contents = userJourney.filter(d => d.employeeID === userId);
    return contents;
  },
);

export const getAnalytics = createSelector([getUserJourneyReport], journey => {
  const cost = round(
    reduce(
      journey.map(d => d.cost),
      (sum, val) => sum + val,
      0,
    ),
  );
  const time = Math.round(
    reduce(
      journey.map(d => d.contentDuration),
      (sum, val) => sum + val,
      0,
    ) / 3600,
  );

  const journeyDuration = reduce(
    journey.map(d => d.contentDuration),
    (sum, val) => sum + val,
    0,
  );

  const journeyDurationCompleted = reduce(
    journey.map(d => (d.contentDuration * d.percentageCompletion) / 100),
    (sum, val) => sum + val,
    0,
  );

  const progress = Math.floor(
    (journeyDurationCompleted * 100) / journeyDuration,
  );
  const courses = journey.filter(d => d.type !== 'book' && d.type !== 'article')
    .length;
  const books = journey.filter(d => d.type === 'book' || d.type === 'article')
    .length;
  return [
    {
      title: 'Milestones',
      value: `${journey.length || 0}`,
      class: 'outlinedCheck',
      icon: 'filter',
    },
    {
      title: 'Duration',
      value: `${time} hrs`,
      class: 'clock',
    },
    {
      title: 'Completion',
      value: `${progress || 0}%`,
      class: 'flag',
    },
    {
      title: 'Cost of Journey',
      value: `$${round(cost / 70)}`,
      class: 'currency',
    },
    // {
    //   title: 'Reading materials',
    //   value: `${books}`,
    //   class: 'ion-ios-book',
    // },
  ];
});



// WEBPACK FOOTER //
// ./src/selectors/viewDemo.js